import { useContext, useMemo } from "react";
import { ProfileLayoutSectionEnum, ILayoutsProfileSection } from "@finbackoffice/fe-core";
import { DocumentStatus, TranslationScopes } from "@finbackoffice/enums";
import { RouteNames } from "utils/constants/routeNames";
import { useLayoutProfileConfig, useUserDocuments } from "@finbackoffice/site-core";
import { CrmContext, ModalsContextV2, UserAccountContextV2 } from "contexts";
import { usePersonalDetailsVerificationConfig } from "hooks/base";
import { AssetsDirectory } from "@finbackoffice/enums";

export type ProfileDataMapType = Partial<Record<ProfileLayoutSectionEnum, ProfileItemDataType>>;

export type ProfileItemDataType = {
    icon?: string;
    iconPath?: string;
    extraIconPath?: string;
    text: { tid: string; namespace?: TranslationScopes };
    href?: string;
    includes?: string;
    isButton?: boolean;
    action?: () => void;
    subItems?: {
        config: ILayoutsProfileSection[];
        data: ProfileDataMapType;
    };
    dataTestId?: string;
};
export const useProfileLayoutViewData = () => {
    const { openWalletModal } = useContext(ModalsContextV2);
    const { openGamificationUi } = useContext(CrmContext);
    const { isUserVerified } = useContext(UserAccountContextV2);
    const { needToVerify } = usePersonalDetailsVerificationConfig();
    const { docStatus } = useUserDocuments();
    const { config: mainSectionLayoutConfig, getFilteredLayoutProfileConfigs } =
        useLayoutProfileConfig(ProfileLayoutSectionEnum.Main, undefined, "profileTablet");
    const { config: messagesSectionLayoutConfig } = useLayoutProfileConfig(
        ProfileLayoutSectionEnum.Messages,
        undefined,
        "profileTablet",
    );
    const { config: betsSectionLayoutConfig } = useLayoutProfileConfig(
        ProfileLayoutSectionEnum.Bets,
        undefined,
        "profileTablet",
    );

    const messagesSectionLayoutConfigMemo = useMemo(
        () => getFilteredLayoutProfileConfigs(messagesSectionLayoutConfig?.config),
        [messagesSectionLayoutConfig, getFilteredLayoutProfileConfigs],
    );
    const mainSectionLayoutConfigMemo = useMemo(
        () => getFilteredLayoutProfileConfigs(mainSectionLayoutConfig?.config),
        [mainSectionLayoutConfig, getFilteredLayoutProfileConfigs],
    );

    const betsSectionLayoutConfigMemo = useMemo(
        () => getFilteredLayoutProfileConfigs(betsSectionLayoutConfig?.config),
        [betsSectionLayoutConfig, getFilteredLayoutProfileConfigs],
    );

    const mainLayoutConfigMap: ProfileDataMapType = useMemo(() => {
        return {
            [ProfileLayoutSectionEnum.Details]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.DETAILS}/${RouteNames.PERSONAL}`,
                includes: `${RouteNames.ACCOUNT}/${RouteNames.DETAILS}/${RouteNames.PERSONAL}`,
                icon: "profile-my-details.svg",
                extraIconPath: isUserVerified
                    ? undefined
                    : `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/question-icon.svg`,
                text: {
                    tid: "account_menu_details_heading",
                },
                dataTestId: "account-menu-item-details",
            },
            [ProfileLayoutSectionEnum.ResponsibleGaming]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.DETAILS}/${RouteNames.RESPONSIBLE_GAMING}`,
                icon: "profile-responsible-gaming.svg",
                text: {
                    tid: "account_menu_details_responsibleGaming",
                },
                dataTestId: "account-menu-item-responsible-gaming",
            },
            [ProfileLayoutSectionEnum["2FA"]]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.DETAILS}/${RouteNames["2FA"]}`,
                icon: "profile-2step.svg",
                text: {
                    tid: "account_menu_details_2fa",
                },
                dataTestId: "account-menu-item-2step",
            },
            [ProfileLayoutSectionEnum.Wallet]: {
                action: () => {
                    openWalletModal();
                },
                icon: "profile-main-wallet.svg",
                text: { tid: "header_wallet", namespace: TranslationScopes.Common },
                dataTestId: "account-popover-menu-wallet",
                isButton: true,
            },
            [ProfileLayoutSectionEnum.Gamification]: {
                action: () => {
                    openGamificationUi();
                },
                icon: "profile-gamification.svg",
                text: {
                    tid: "account_menu_gamification_heading",
                },
            },
            [ProfileLayoutSectionEnum.Verification]: {
                iconPath: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-identity_document.svg`,
                href: `/${RouteNames.ACCOUNT}/${RouteNames.DETAILS}/${RouteNames.PERSONAL}/${RouteNames.VERIFICATION}`,
                extraIconPath:
                    needToVerify ||
                    Object.values(docStatus).some((status) => status === DocumentStatus.REJECTED)
                        ? `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/question-icon.svg`
                        : undefined,
                text: {
                    tid: "account_details_verification_heading",
                },
            },
        };
    }, [openWalletModal, openGamificationUi, isUserVerified, docStatus, needToVerify]);

    const messagesLayoutConfigMap: ProfileDataMapType = useMemo(() => {
        return {
            [ProfileLayoutSectionEnum.Inbox]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.MESSAGES}/${RouteNames.INBOX}`,
                includes: `${RouteNames.ACCOUNT}/${RouteNames.MESSAGES}/${RouteNames.INBOX}`,
                icon: "profile-messages-inbox.svg",
                text: { tid: "account_menu_messages_inbox" },
            },
            [ProfileLayoutSectionEnum.Sent]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.MESSAGES}/${RouteNames.SENT}`,
                includes: `${RouteNames.ACCOUNT}/${RouteNames.MESSAGES}/${RouteNames.SENT}`,
                icon: "profile-messages-sent.svg",
                text: { tid: "account_menu_messages_sent" },
                dataTestId: "account-menu-item-sent",
            },
            [ProfileLayoutSectionEnum.Compose]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.MESSAGES}/${RouteNames.COMPOSE}`,
                icon: "profile-messages-compose.svg",
                text: { tid: "account_menu_messages_compose" },
                dataTestId: "account-menu-item-compose",
            },
        };
    }, []);

    const betsLayoutConfigMap: ProfileDataMapType = useMemo(() => {
        return {
            [ProfileLayoutSectionEnum.Sport]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.BETS}/${RouteNames.SPORT}`,
                icon: `profile-sport.svg`,
                text: { tid: "account_menu_bets_sport_heading" },
            },
            [ProfileLayoutSectionEnum.Livecasino]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.BETS}/${RouteNames.LIVE_CASINO}`,
                icon: `profile-live-casino.svg`,
                text: { tid: "account_menu_bets_livecasino_heading" },
                dataTestId: "account-menu-item-livecasino-bets",
            },
            [ProfileLayoutSectionEnum.Slots]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.BETS}/${RouteNames.SLOTS}`,
                icon: `profile-slots.svg`,
                text: { tid: "account_menu_bets_slots_heading" },
                dataTestId: "account-menu-item-slot-bets",
            },
        };
    }, []);

    const referralLayoutConfigMap: ProfileDataMapType = useMemo(() => {
        return {
            [ProfileLayoutSectionEnum.Sport]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.REFERRAL}/${RouteNames.SPORT}`,
                icon: `profile-sport.svg`,
                text: { tid: "account_menu_referral_heading_sport" },
            },
            [ProfileLayoutSectionEnum.Livecasino]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.REFERRAL}/${RouteNames.LIVE_CASINO}`,
                icon: `profile-live-casino.svg`,
                text: { tid: "account_menu_referral_heading_livecasino" },
            },
            [ProfileLayoutSectionEnum.Slots]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.REFERRAL}/${RouteNames.SLOTS}`,
                icon: `profile-slots.svg`,
                text: { tid: "account_menu_referral_heading_slots" },
            },
        };
    }, []);

    const becomeReferralLayoutConfigMap: ProfileDataMapType = useMemo(() => {
        return {
            [ProfileLayoutSectionEnum.Sport]: {
                href: `/${RouteNames.ACCOUNT}/${RouteNames.REFERRAL}/${RouteNames.SPORT}`,
                includes: `${RouteNames.ACCOUNT}/${RouteNames.REFERRAL}`,
                iconPath: `/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Profile}/profile-referral.svg`,
                text: { tid: "account_menu_referral_heading_become_referral" },
            },
        };
    }, []);

    const layoutConfigMap: Partial<Record<ProfileLayoutSectionEnum, ProfileItemDataType>> =
        useMemo(() => {
            return {
                [ProfileLayoutSectionEnum.Main]: {
                    href: `/${RouteNames.ACCOUNT}/${RouteNames.DETAILS}/${RouteNames.PERSONAL}`,
                    includes: `${RouteNames.ACCOUNT}/${RouteNames.DETAILS}`,
                    icon: "profile-main.svg",
                    extraIconPath: isUserVerified
                        ? undefined
                        : `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/question-icon.svg`,
                    text: { tid: "account_menu_main_heading" },
                    subItems: {
                        config: mainSectionLayoutConfigMemo,
                        data: mainLayoutConfigMap,
                    },
                },
                [ProfileLayoutSectionEnum.Bets]: {
                    href: `/${RouteNames.ACCOUNT}/${RouteNames.BETS}/${betsSectionLayoutConfigMemo[0].name}`,
                    includes: `${RouteNames.ACCOUNT}/${RouteNames.BETS}`,
                    icon: "profile-my-bets.svg",
                    text: { tid: "account_menu_bets_heading" },
                    subItems: {
                        config: betsSectionLayoutConfigMemo,
                        data: betsLayoutConfigMap,
                    },
                    dataTestId: "account-compact-menu-bets",
                },
                // [ProfileLayoutSectionEnum.Agent]: {
                //     href: `/${RouteNames.ACCOUNT}/${RouteNames.AGENT}`,
                //     icon: "profile-agent.svg",
                //     text: { tid: "account_agent_agent" },
                // },
                [ProfileLayoutSectionEnum.Referral]: {
                    href: `/${RouteNames.ACCOUNT}/${RouteNames.REFERRAL}/${RouteNames.SPORT}`,
                    includes: `${RouteNames.ACCOUNT}/${RouteNames.REFERRAL}`,
                    iconPath: `/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Profile}/profile-referral.svg`,
                    text: {
                        tid: "account_menu_referral_heading",
                    },
                    dataTestId: "account-menu-item-referral",
                },
                [ProfileLayoutSectionEnum.Messages]: {
                    href: `/${RouteNames.ACCOUNT}/${RouteNames.MESSAGES}/${messagesSectionLayoutConfigMemo[0].name}`,
                    includes: `${RouteNames.ACCOUNT}/${RouteNames.MESSAGES}`,
                    icon: "profile-messages.svg",
                    text: { tid: "account_menu_messages_heading" },
                    subItems: {
                        config: messagesSectionLayoutConfigMemo,
                        data: messagesLayoutConfigMap,
                    },
                    dataTestId: "account-compact-menu-messages",
                },
                [ProfileLayoutSectionEnum.ChatService]: {
                    href: `/${RouteNames.ACCOUNT}/${RouteNames.LIVE}`,
                    icon: "profile-livechat.svg",
                    text: { tid: "account_menu_chat_service_heading" },
                },
                [ProfileLayoutSectionEnum.Wallet]: {
                    action: () => {
                        openWalletModal();
                    },
                    icon: "profile-wallet.svg",
                    text: { tid: "header_wallet", namespace: TranslationScopes.Common },
                },
                [ProfileLayoutSectionEnum.Gamification]: {
                    action: () => {
                        openGamificationUi();
                    },
                    icon: "profile-gamification.svg",
                    text: {
                        tid: "account_menu_gamification_heading",
                        namespace: TranslationScopes.UserAccount,
                    },
                },
                [ProfileLayoutSectionEnum.Transactions]: {
                    href: `/${RouteNames.ACCOUNT}/${RouteNames.TRANSACTIONS}`,
                    iconPath: `/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Profile}/profile-balance.svg`,
                    text: {
                        tid: "account_menu_transactions_heading",
                    },
                    dataTestId: "account-menu-item-transactions",
                },
            };
        }, [
            openWalletModal,
            openGamificationUi,
            mainSectionLayoutConfigMemo,
            mainLayoutConfigMap,
            messagesSectionLayoutConfigMemo,
            messagesLayoutConfigMap,
            betsSectionLayoutConfigMemo,
            betsLayoutConfigMap,
            isUserVerified,
        ]);

    const value = useMemo(() => {
        return {
            mainLayoutConfigMap,
            layoutConfigMap,
            referralLayoutConfigMap,
            becomeReferralLayoutConfigMap,
        };
    }, [
        mainLayoutConfigMap,
        layoutConfigMap,
        referralLayoutConfigMap,
        becomeReferralLayoutConfigMap,
    ]);

    return value;
};
